import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WarningIcon } from '@phosphor-icons/react';
import { UpdateColumn, ProjectColumn } from 'shared/types';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useColumnMutations } from '@/hooks/useColumnMutations';
import { defineModal } from '@/lib/modals';

import { COLUMN_COLOR_OPTIONS } from '@/constants/columnColors';

export interface EditColumnDialogProps {
  column: ProjectColumn;
}

export type EditColumnDialogResult =
  | { status: 'saved'; column: ProjectColumn }
  | { status: 'canceled' };

const EditColumnDialogImpl = NiceModal.create<EditColumnDialogProps>(
  ({ column }) => {
    const { t } = useTranslation(['settings', 'common']);
    const modal = useModal();
    const [name, setName] = useState(column.name);
    const [color, setColor] = useState(column.color);

    // Reset form when dialog opens
    useEffect(() => {
      if (modal.visible) {
        setName(column.name);
        setColor(column.color);
      }
    }, [modal.visible, column.name, column.color]);

    const { updateColumn } = useColumnMutations({
      onUpdateSuccess: (updatedColumn) => {
        modal.resolve({
          status: 'saved',
          column: updatedColumn,
        } as EditColumnDialogResult);
        modal.hide();
      },
      onUpdateError: () => {},
    });

    const handleSave = () => {
      if (!name.trim()) return;

      const updateData: UpdateColumn = {
        name: name.trim(),
        color,
        position: null,
      };

      updateColumn.mutate({ columnId: column.id, data: updateData });
    };

    const handleCancel = () => {
      modal.resolve({ status: 'canceled' } as EditColumnDialogResult);
      modal.hide();
    };

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && name.trim() && !updateColumn.isPending) {
        e.preventDefault();
        handleSave();
      }
    };

    return (
      <Dialog open={modal.visible} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings.columns.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('settings.columns.edit.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="column-name">
                {t('settings.columns.edit.nameLabel')}
              </Label>
              <Input
                id="column-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('settings.columns.edit.namePlaceholder')}
                autoFocus
                disabled={updateColumn.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="column-color">
                {t('settings.columns.edit.colorLabel')}
              </Label>
              <div className="grid grid-cols-7 gap-2">
                {COLUMN_COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    disabled={updateColumn.isPending}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === c.value
                        ? 'border-foreground scale-110'
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.color }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
          </div>

          {updateColumn.isError && (
            <Alert variant="destructive">
              <WarningIcon className="h-4 w-4" />
              <AlertDescription>
                {updateColumn.error instanceof Error
                  ? updateColumn.error.message
                  : t('settings.columns.edit.errors.updateFailed')}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={updateColumn.isPending}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || updateColumn.isPending}
            >
              {updateColumn.isPending
                ? t('common:states.saving')
                : t('common:buttons.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

export const EditColumnDialog = defineModal<
  EditColumnDialogProps,
  EditColumnDialogResult
>(EditColumnDialogImpl);
